import type { ReactNode } from 'react';
import { Card } from '../ui';

type Tone = 'sage' | 'rose' | 'ink';

type Props = {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  icon?: ReactNode;
  iconTone?: Tone;
  delta?: {
    label: ReactNode;     // fx "↑ 12,4%" eller "↑ 5"
    direction: 'up' | 'down' | 'neutral';
  };
  caption?: ReactNode;     // fx "vs. forrige mdr."
  loading?: boolean;
};

const iconBg: Record<Tone, string> = {
  sage: 'bg-sage-100 text-sage-700',
  rose: 'bg-rose-100 text-rose-700',
  ink:  'bg-cream-200 text-ink-700',
};

const deltaCls = {
  up:      'bg-sage-100 text-sage-700',
  down:    'bg-rose-100 text-rose-700',
  neutral: 'bg-cream-200 text-ink-500',
};

export default function KpiCard({
  label, value, unit, icon, iconTone = 'sage',
  delta, caption, loading = false,
}: Props) {
  return (
    <Card padded className="h-full flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-base font-semibold text-ink-900 tracking-tight leading-snug">{label}</h3>
        {icon && (
          <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconBg[iconTone]}`}>
            {icon}
          </span>
        )}
      </div>

      {loading ? (
        <div className="h-7 w-24 bg-ink-100 rounded animate-pulse" />
      ) : (
        <div className="text-[28px] font-semibold tracking-tight text-ink-900 leading-none">
          {value}
          {unit && <span className="text-[13px] font-normal text-ink-500 ml-1.5">{unit}</span>}
        </div>
      )}

      {(delta || caption) && (
        <div className="mt-3 flex items-center gap-2 text-xs text-ink-500">
          {delta && (
            <span className={`font-semibold inline-flex items-center px-1.5 py-0.5 rounded-md text-[11.5px] ${deltaCls[delta.direction]}`}>
              {delta.label}
            </span>
          )}
          {caption && <span>{caption}</span>}
        </div>
      )}
    </Card>
  );
}
